import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2Icon } from "lucide-react";
import type { ReactNode } from "react";

interface DeleteConfirmDialogProps {
  /** The title shown in the dialog header */
  title?: string;
  /** Description explaining what will be deleted */
  description: string;
  /** Text for the delete button. Defaults to "Delete" */
  deleteLabel?: string;
  /** Called when user confirms deletion */
  onConfirm: () => void;
  /** Whether the delete action is in progress */
  isDeleting?: boolean;
  /** Whether the delete button should be disabled */
  disabled?: boolean;
  /** Custom trigger element. If not provided, renders a trash icon button */
  trigger?: ReactNode;
  /** Size of the default trigger button */
  triggerSize?: "default" | "sm" | "lg" | "icon";
  /** Variant of the default trigger button */
  triggerVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
}

/**
 * A reusable delete confirmation dialog.
 * Wraps AlertDialog with sensible defaults for delete actions.
 */
export function DeleteConfirmDialog({
  title = "Delete",
  description,
  deleteLabel = "Delete",
  onConfirm,
  isDeleting = false,
  disabled = false,
  trigger,
  triggerSize = "icon",
  triggerVariant = "ghost",
}: DeleteConfirmDialogProps) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {trigger ?? (
          <Button
            variant={triggerVariant}
            size={triggerSize}
            disabled={disabled || isDeleting}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2Icon className="w-4 h-4" />
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? "Deleting..." : deleteLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
